export function getAccessToken() {
  return localStorage.getItem("access_token");
}

/**
 * Get subscription-status URL with Stripe checkout session_id if present in URL.
 * Used when user returns from Stripe checkout - backend syncs from session before returning status.
 */
export function getSubscriptionStatusUrl() {
  const params = new URLSearchParams(window.location.search);
  const sid = params.get("session_id");
  if (sid && sid.startsWith("cs_")) {
    return `/api/auth/subscription-status?session_id=${encodeURIComponent(sid)}`;
  }
  return "/api/auth/subscription-status";
}

export function getRefreshToken() {
  return localStorage.getItem("refresh_token");
}

export function setTokens(accessToken, refreshToken) {
  if (accessToken) {
    localStorage.setItem("access_token", accessToken);
  }
  if (refreshToken) {
    localStorage.setItem("refresh_token", refreshToken);
  }
}

export function clearTokens() {
  localStorage.removeItem("access_token");
  localStorage.removeItem("refresh_token");
}

/**
 * Refresh the access token using the refresh token.
 * Returns new access token or null if refresh fails.
 */
export async function refreshAccessToken() {
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    console.log("[Auth] No refresh token available");
    return null;
  }

  try {
    const response = await fetch('/api/auth/refresh', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        refresh_token: refreshToken
      })
    });

    const result = await response.json();
    
    if (result.status === 'success' && result.access_token) {
      localStorage.setItem('access_token', result.access_token);
      console.log('[Auth] Access token refreshed successfully');
      return result.access_token;
    } else {
      console.error('[Auth] Token refresh failed:', result);
      clearTokens();
      return null;
    }
  } catch (error) {
    console.error('[Auth] Error refreshing token:', error);
    clearTokens();
    return null;
  }
}

/**
 * Get auth headers synchronously (for immediate use).
 * Use authenticatedFetch() for automatic token refresh on 401 errors.
 */
export function getAuthHeaders() {
  const token = getAccessToken();
  const headers = {};
  if (token) {
    // IMPORTANT: The Lambda Function URL with AWS_IAM + CloudFront uses the
    // `Authorization` header for SigV4 signing. To avoid interfering with that,
    // we send the app JWT in a custom header instead.
    headers["X-Access-Token"] = token;
    console.log("[Auth] Token found, adding X-Access-Token header to request");
  } else {
    console.log("[Auth] No token found in localStorage, request will be unauthenticated");
  }
  return headers;
}

/**
 * Fetch wrapper that automatically refreshes token on 401 errors.
 * Use this instead of fetch() for authenticated requests.
 */
export async function authenticatedFetch(url, options = {}) {
  // Make initial request with current access token
  let headers = {
    ...getAuthHeaders(),
    ...(options.headers || {})
  };
  
  let response = await fetch(url, {
    ...options,
    headers
  });
  
  // If we get 401, try to refresh token and retry once
  if (response.status === 401) {
    console.log('[Auth] Got 401, attempting to refresh token');
    const newToken = await refreshAccessToken();
    
    if (newToken) {
      // Retry request with new token
      headers = {
        ...options.headers || {},
        'X-Access-Token': newToken
      };
      response = await fetch(url, {
        ...options,
        headers
      });
    } else {
      // Refresh failed: clear auth state and redirect to sign-in (unless on share page – shared links are viewable without login)
      console.log('[Auth] Token refresh failed, clearing auth');
      if (!window.location.pathname.startsWith('/share/')) {
        window.dispatchEvent(new CustomEvent('sessionExpired'));
      }
    }
  }
  
  return response;
}
